import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Combine, Loader2, Download, ShieldCheck, ShieldAlert, AlertTriangle, X } from "lucide-react";

// Differential merge tool: pick ≥2 solutions built from the same ORI (e.g. Stage1 + AdBlue)
// and the engine pre-assembles a combined file (with byte-region conflict detection).
export const SolutionCombiner = ({ group }) => {
  const diffable = useMemo(() => (group.variants || []).filter((s) => s.diff_mode === "diff"), [group.variants]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (diffable.length < 2) return null;

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const combine = async () => {
    if (sel.length < 2) return toast.error("Sélectionnez au moins 2 solutions à fusionner");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/solutions/combine", { solution_ids: sel });
      setResult(data);
      if (data.ok) toast.success(`Fichier pré-assemblé · ${data.changed_bytes} octets modifiés`);
      else toast.error(`Conflit de zones détecté (${data.conflicts.length}) — fusion refusée`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de fusion");
    } finally { setBusy(false); }
  };

  const download = async (token, filename) => {
    try {
      const { data } = await api.get(`/admin/solutions/combined/${token}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "combined.bin"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <div className="px-6 pb-3 pl-10">
      {!open ? (
        <button
          data-testid={`combine-open-${(group.hash || "").slice(0, 8)}`}
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-aurora-purple border border-aurora-purple/40 hover:bg-aurora-purple/10 px-3 py-1.5 rounded-sm transition-colors"
        >
          <Combine className="w-3.5 h-3.5" /> Combiner des solutions (ex. Stage + AdBlue)
        </button>
      ) : (
        <div className="glass rounded-md p-4 border border-aurora-purple/30" data-testid="combiner-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-tight">
              <Combine className="w-4 h-4 text-aurora-purple" /> Fusion différentielle
            </div>
            <button onClick={() => { setOpen(false); setSel([]); setResult(null); }} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <p className="font-mono text-[10px] text-zinc-500 mb-3 leading-snug">
            Sélectionnez 2 solutions (ou plus) de ce même fichier original. Le moteur applique les deux différentiels sur l'ORI et détecte tout <b className="text-white">conflit de zones</b>. Le checksum est recalculé si un algorithme validé existe, sinon le fichier pré-assemblé est à valider par le tuner.
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {diffable.map((s) => {
              const on = sel.includes(s.id);
              return (
                <button
                  key={s.id}
                  data-testid={`combine-pick-${s.id}`}
                  onClick={() => toggle(s.id)}
                  className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest rounded-sm border transition-colors ${on ? "border-aurora-purple text-aurora-purple bg-aurora-purple/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
                >
                  {(s.services || []).join(" + ") || s.ref}
                </button>
              );
            })}
          </div>
          <button
            data-testid="combine-run-btn"
            onClick={combine}
            disabled={busy || sel.length < 2}
            className="flex items-center gap-2 px-5 py-2.5 bg-aurora-purple text-white font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-purple-500 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Combine className="w-4 h-4" />} Fusionner ({sel.length})
          </button>

          {result && result.ok && (
            <div className="mt-4 rounded-sm border border-signal-success/30 bg-signal-success/5 p-4" data-testid="combine-result-ok">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-signal-success" />
                <span className="font-display text-sm font-bold uppercase text-signal-success">Fichier pré-assemblé prêt</span>
              </div>
              <div className="font-mono text-[11px] text-zinc-300 space-y-1 mb-3">
                <div>{result.changed_bytes} octets modifiés · famille {result.family || "—"} · {(result.size / 1024).toFixed(1)} Ko</div>
                {result.checksum_ok ? (
                  <div className="text-signal-success flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Checksum recalculé automatiquement</div>
                ) : (
                  <div className="text-signal-processing flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Checksum à valider par le tuner (aucun algorithme validé enregistré pour cette famille)</div>
                )}
              </div>
              <button
                data-testid="combine-download-btn"
                onClick={() => download(result.download_token, result.filename)}
                className="flex items-center gap-2 px-4 py-2 bg-signal-success text-black font-display font-bold uppercase tracking-wider text-xs rounded-sm hover:opacity-90 transition-opacity"
              >
                <Download className="w-3.5 h-3.5" /> Télécharger le fichier combiné
              </button>
            </div>
          )}

          {result && !result.ok && (
            <div className="mt-4 rounded-sm border border-signal-error/30 bg-signal-error/5 p-4" data-testid="combine-result-conflict">
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="w-4 h-4 text-signal-error" />
                <span className="font-display text-sm font-bold uppercase text-signal-error">Conflit de zones — fusion refusée</span>
              </div>
              <p className="font-mono text-[10px] text-zinc-400 mb-2">
                Ces solutions modifient les <b className="text-white">mêmes octets</b> de façon incompatible. Une fusion automatique briquerait le calculateur — traitement manuel requis.
              </p>
              <div className="font-mono text-[10px] text-zinc-300 space-y-0.5 max-h-32 overflow-auto">
                {result.conflicts.slice(0, 8).map((c, i) => (
                  <div key={i}>Offset {c.hex} : {c.a.ref} (0x{c.a.byte.toString(16)}) ↔ {c.b.ref} (0x{c.b.byte.toString(16)})</div>
                ))}
                {result.conflicts.length > 8 && <div className="text-zinc-500">+ {result.conflicts.length - 8} autres…</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
